import StaffNoteWithdrawalDialog from "./StaffNoteWithdrawalDialog";
import React, { useEffect, useState } from "react";
import { Alert } from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";
import { getStaffOriginal, mutateStaffNote } from "./staffNotesService";
import StaffNoteEditor from "./StaffNoteEditor";

export default function useStaffTimelineOriginalActions({
  protection,
  childId,
  child,
  currentUserRole,
}) {
  const [user] = useAuthState(auth);
  const [editing, setEditing] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const uid = user?.uid,
    sourceId = protection.sourceId;
  useEffect(() => {
    setEditing(null);
    setError("");
  }, [uid, sourceId, childId]);
  const source = protection.source;
  const authorId = source?.authorId || source?.createdBy;
  const enabled =
    !child?.staffCaregivers || child.staffCaregivers[uid]?.enabled === true;
  const canMutate =
    protection.staff &&
    !protection.blocked &&
    !protection.signed &&
    Boolean(
      uid && authorId === uid && currentUserRole === "caregiver" && enabled,
    );
  const open = async (operation) => {
    if (!canMutate || busy) return;
    setBusy(true);
    setError("");
    try {
      const { original } = await getStaffOriginal(sourceId);
      if (
        !original?.version ||
        original.captureId !== sourceId ||
        original.childId !== childId ||
        original.authorId !== uid
      )
        throw new Error(
          "The original could not be verified. Refresh the Timeline.",
        );
      if (original.signature || original.withdrawn)
        throw new Error(
          "This original is signed or withdrawn. Refresh the Timeline; signed notes require a dated correction.",
        );
      setEditing({ note: original, operation, uid });
    } catch (failure) {
      setError(failure.message || "The original could not be opened.");
    } finally {
      setBusy(false);
    }
  };
  const save = async (changes) => {
    await mutateStaffNote({
      childId,
      captureId: editing.note.captureId,
      expectedVersionId: editing.note.version.versionId,
      operation: editing.operation,
      ...changes,
    });
    for (const name of [
      "captureez:timeline-refresh",
      "captureez:staff-notes-refresh",
    ])
      window.dispatchEvent(new CustomEvent(name, { detail: { childId } }));
  };
  const current =
    editing?.uid === uid &&
    editing?.note.captureId === sourceId &&
    editing?.note.childId === childId;
  const editor = (
    <>
      {error && !current ? <Alert severity="warning">{error}</Alert> : null}
      {current && editing.operation === "edit" ? (
        <StaffNoteEditor
          key={editing.note.version.versionId}
          note={editing.note}
          operation="edit"
          onSave={save}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {current && editing.operation === "withdraw" ? (
        <StaffNoteWithdrawalDialog
          note={editing.note}
          onWithdraw={() => save({})}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
  return {
    canMutate,
    busy,
    onEdit: () => open("edit"),
    onDelete: () => open("withdraw"),
    editor,
  };
}
